// The two questions the app asks about a moved view, side by side, because they
// share a word and are not the same question (#226).
//
// „ja, teraz" is the view this app opens on: the centre following the resident
// where they allowed it, the clock running. Everything else is a departure from
// it — but a departure the RESIDENT made and a departure the ADDRESS carries
// are answered differently, and until now each site spelled its own condition
// inline with `moved` in the comment above it.
//
// Pure, and about a `ViewState` alone: nothing here asks the browser anything.
import type { ViewState } from './viewstate.ts';

/**
 * The resident picked a centre, or borrowed an hour — the view the „wróć do:
 * ja, teraz" controls exist to leave.
 *
 * `{ kind: 'me' }` is **not** a move: it IS „ja", and a resident who granted
 * geolocation was once being offered a permanent button named „Wróć do: ja,
 * teraz" whose effect was to leave „ja" (`wheel.ts`, `kreska.ts`).
 *
 * Read by the views: the crosshair's visibility, the centre mark's shape, the
 * kreska's `◇` glyph and its „Środek przeniesiony" name.
 */
export function movedByHand(view: ViewState): boolean {
  return view.centre.kind === 'picked' || view.at != null;
}

/**
 * The view is not the default here-and-now — `{ kind: 'me' }` included.
 *
 * Read by history: `viewState.back()` at depth 0 unwinds one opening, and
 * following the resident IS an opening (`tu=ja` goes in the address and Back
 * takes it off again, §4.2). Wider than {@link movedByHand} by exactly that
 * one case, and that one case is the whole of #226.
 */
export function awayFromNow(view: ViewState): boolean {
  return view.centre.kind !== 'default' || view.at != null;
}
